import React, { useEffect, useRef, useState } from "react";

import Button, { ButtonTypes } from "../../Button";
import {
  OTHER_EXCHANGE,
  RequestableExchange,
  useGetRequestableExchangesQuery,
  useSubmitTickerRequestMutation,
} from "../../../services/tickerRequests";
import { onEnter } from "../../../utils/keyboard";
import { toErrorMessage } from "./errors";
import {
  Disclosure,
  DisclosureField,
  DisclosureGrid,
  DisclosureHelper,
  DisclosureInput,
  DisclosureSelect,
  DisclosureTextArea,
  DisclosureTitle,
  ErrorMessage,
} from "./styles";
import { TickerRequestFormProps } from "./types";

const exchangeOptionLabel = ({ code, name }: RequestableExchange) =>
  name === code ? code : `${name} (${code})`;

export const TickerRequestForm: React.FC<TickerRequestFormProps> = ({
  initialSymbol,
  onSubmitted,
}) => {
  const [symbol, setSymbol] = useState(initialSymbol);
  const [exchange, setExchange] = useState("");
  const [otherExchangeName, setOtherExchangeName] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isMounted = useRef(true);
  // Set on setup too: StrictMode's simulated unmount would otherwise leave it false for good.
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const {
    data: exchanges,
    isLoading: exchangesLoading,
    isError: exchangesFailed,
  } = useGetRequestableExchangesQuery();
  const [submitTickerRequest, { isLoading: isSubmitting }] =
    useSubmitTickerRequestMutation();

  const isOther = exchange === OTHER_EXCHANGE;
  const trimmedSymbol = symbol.trim();
  const trimmedOtherName = otherExchangeName.trim();
  const canSubmit =
    trimmedSymbol.length > 0 &&
    exchange.length > 0 &&
    (!isOther || trimmedOtherName.length > 0) &&
    !isSubmitting;

  const exchangeOptions = exchanges ?? [];
  const exchangePlaceholder = exchangesLoading
    ? "Loading exchanges…"
    : "Choose an exchange";

  const editing =
    (set: (value: string) => void) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      setError(null);
      set(event.target.value);
    };

  const handleSubmit = async () => {
    setError(null);
    try {
      const submitted = await submitTickerRequest({
        symbol: trimmedSymbol,
        exchange,
        ...(isOther ? { otherExchangeName: trimmedOtherName } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }).unwrap();
      if (isMounted.current) {
        onSubmitted(submitted.symbol);
      }
    } catch (err: unknown) {
      if (isMounted.current) {
        setError(toErrorMessage(err));
      }
    }
  };

  // The fields sit inside the host Buy form, so Enter must send the request rather than submit the buy.
  const handleKeyDown = onEnter(() => {
    if (canSubmit) {
      void handleSubmit();
    }
  });

  return (
    <Disclosure>
      <DisclosureTitle>Request a ticker</DisclosureTitle>
      <DisclosureGrid>
        <DisclosureField>
          Symbol *
          <DisclosureInput
            value={symbol}
            onChange={editing(setSymbol)}
            onKeyDown={handleKeyDown}
            placeholder="VWCE"
            maxLength={20}
          />
        </DisclosureField>
        <DisclosureField>
          Exchange *
          <DisclosureSelect
            value={exchange}
            onChange={editing(setExchange)}
            onKeyDown={handleKeyDown}
            disabled={exchangesLoading}
          >
            <option value="" disabled>
              {exchangePlaceholder}
            </option>
            {exchangeOptions.map((option) => (
              <option key={option.code} value={option.code}>
                {exchangeOptionLabel(option)}
              </option>
            ))}
            <option value={OTHER_EXCHANGE}>Other</option>
          </DisclosureSelect>
        </DisclosureField>
      </DisclosureGrid>

      {exchangesFailed && (
        <ErrorMessage role="alert">
          The exchange list couldn&apos;t be loaded. Choose Other and type the
          exchange name.
        </ErrorMessage>
      )}

      {isOther && (
        <DisclosureField>
          Exchange name *
          <DisclosureInput
            value={otherExchangeName}
            onChange={editing(setOtherExchangeName)}
            onKeyDown={handleKeyDown}
            placeholder="Nasdaq Helsinki"
            maxLength={60}
          />
        </DisclosureField>
      )}

      <DisclosureField>
        Request note
        <DisclosureTextArea
          value={note}
          onChange={editing(setNote)}
          placeholder="Anything that helps us find it, e.g. the ISIN"
          maxLength={500}
          rows={2}
        />
      </DisclosureField>

      <DisclosureHelper>
        We review each request and email you once the ticker is added or
        declined.
      </DisclosureHelper>

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      <Button
        type={ButtonTypes.primary}
        buttonType="button"
        disabled={!canSubmit}
        onClick={() => void handleSubmit()}
      >
        {isSubmitting ? "Sending…" : "Send request"}
      </Button>
    </Disclosure>
  );
};
